import { Row } from "react-grid-system";
import styled from "styled-components";
import { theme } from "../../../theme";
import { OpenLinkIcon } from "../../atoms/icons/OpenLinkIcon";
import BodySmall from "../../atoms/typography/BodySmall";
import { useTranslation } from "react-i18next";
import { useClientCreditData } from "../../../graphql/hooks/client/useClientCreditData";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import Alert from "../Alert";
import { formatPrice } from "../../../utils/formatters";
import { useState } from "react";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import Subheading from "../../atoms/typography/Subheading";

interface Props {
  clientIdentifier: string;
  expandable?: boolean;
  hideClientCardLinkButton?: boolean;
}

const FlexedDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const InfoRow = styled.div`
  width: 240px;
  padding: 4px;
`;

const GreyContainer = styled.div<{ $expandable?: boolean }>`
  border-radius: 12px;
  border: ${(props) =>
    props.$expandable ? `1px solid ${theme.colors.grey05}` : "none"};
  padding: ${(props) => (props.$expandable ? "16px" : "0px")};
`;

const CreditScoreOverviewSmall: React.FC<Props> = ({
  clientIdentifier,
  expandable,
  hideClientCardLinkButton,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(expandable ? false : true);

  const { clientCreditData, error, loading } =
    useClientCreditData(clientIdentifier);

  return (
    <div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <GreyContainer $expandable={expandable}>
          <Row nogutter align="center" justify="between">
            <Subheading color={theme.colors.primary}>
              {t("invoiceAssessment.creditScoreDetails")}
            </Subheading>
            <FlexedDiv>
              {!hideClientCardLinkButton && (
                <a
                  href={`/client/${clientIdentifier}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OpenLinkIcon color={theme.colors.primary} />
                </a>
              )}
              {expandable && (
                <ChevronButton
                  type={isOpen ? "up" : "down"}
                  onClick={() => setIsOpen(!isOpen)}
                />
              )}
            </FlexedDiv>
          </Row>

          <SlideOutHeight isVisible={isOpen}>
            <BodySmall color={theme.colors.textSecondary}>
              <i>{t("invoiceAssessment.sourceCreditsafe")}</i>
            </BodySmall>
            <FlexedDiv>
              <InfoRow>
                <BodySmall fontweight={500}>
                  {t("invoiceAssessment.currentRating")}
                </BodySmall>
              </InfoRow>
              <InfoRow>
                <BodySmall>
                  {clientCreditData?.creditData?.creditRating?.legacyRating}
                </BodySmall>
              </InfoRow>
            </FlexedDiv>
            <FlexedDiv>
              <InfoRow>
                <BodySmall fontweight={500}>
                  {t("creditData.creditSafeScore")}
                </BodySmall>
              </InfoRow>
              <InfoRow>
                <BodySmall>
                  {clientCreditData?.creditData?.creditRating?.creditsafeScore}
                </BodySmall>
              </InfoRow>
            </FlexedDiv>
            <FlexedDiv>
              <InfoRow>
                <BodySmall fontweight={500}>
                  {t("creditData.creditLimit")}
                </BodySmall>
              </InfoRow>
              <InfoRow>
                <BodySmall>
                  {formatPrice(
                    clientCreditData?.creditData?.creditRating?.creditLimit,
                  )}
                </BodySmall>
              </InfoRow>
            </FlexedDiv>
            <FlexedDiv>
              <InfoRow>
                <BodySmall fontweight={500}>
                  {t("invoiceAssessment.currentDebtBalance")}
                </BodySmall>
              </InfoRow>
              <InfoRow>
                <BodySmall>
                  {formatPrice(
                    clientCreditData?.creditData?.negativeInformation
                      ?.debtBalance?.totalBalance,
                  )}
                </BodySmall>
              </InfoRow>
            </FlexedDiv>
            <FlexedDiv>
              <InfoRow>
                <BodySmall fontweight={500}>
                  {t("invoiceAssessment.paymentDefaults")}
                </BodySmall>
              </InfoRow>
              <InfoRow>
                <BodySmall>
                  {clientCreditData?.creditData?.negativeInformation
                    ?.recordOfNonPayment?.numberOfApplications ?? 0}
                </BodySmall>
              </InfoRow>
            </FlexedDiv>
            <FlexedDiv>
              <InfoRow>
                <BodySmall fontweight={500}>
                  {t("invoiceAssessment.debtCollectionOrderAppliccation")}
                </BodySmall>
              </InfoRow>
              <InfoRow>
                <BodySmall>
                  {clientCreditData?.creditData?.negativeInformation
                    ?.publicClaims?.length ?? "-"}
                </BodySmall>
              </InfoRow>
            </FlexedDiv>
          </SlideOutHeight>
          {error && (
            <Alert
              type="error"
              title="Kunde inte hämta kreditupplysningen"
              message={error.message}
              small={expandable ? true : false}
            />
          )}
        </GreyContainer>
      )}
    </div>
  );
};

export default CreditScoreOverviewSmall;
